import React, { useState, useEffect, useMemo } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import { useStaticOptions } from "../context/StaticOptionsContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";

const TobbSzinu = () => {
    const { order, updateOrder } = useOrder();
    const navigate = useStepNavigate();
    const { saveOrder } = useOrderSave();
    const { staticOptions } = useStaticOptions();

    const colorsAndMaterials = order.model_options?.colors_and_materials_for_model || null;
    const shoeColorOptionsRaw = staticOptions?.shoe_color_options || [];

    const slugify = (s) =>
        String(s || "")
            .toLowerCase()
            .replace(/[^a-z0-9áéíóúüűöő\s-]/g, "")
            .trim()
            .replace(/\s+/g, "_");

    const normalizeShoeColorOptions = (arr) =>
    (arr || []).map((c, idx) => {
        const colorName =
            c.cselicolor_color_name ||
            c.cselicolor_color_national ||
            c.cselicolor_color_code ||
            "Ismeretlen szín";

        const leatherName = c.cselicolor_top_leather_name || "Ismeretlen anyag";

        return {
            id: `${c.cselicolor_szam ?? "x"}_${c.cselicolor_top_leather_szam ?? "x"}_${c.cselicolor_color_szam ?? "x"}_${idx}`,
            color_text: `${colorName} / ${leatherName}`,
            color_1: Number(c.cselicolor_color_1) === 1,
            color_2: Number(c.cselicolor_color_2) === 1,
            color_3: Number(c.cselicolor_color_3) === 1,
            baseId: Number(c.cselicolor_szam) || null
        };
    });

    const shoe_colors = useMemo(
        () => normalizeShoeColorOptions(shoeColorOptionsRaw),
        [shoeColorOptionsRaw]
    );

    const [loading, setLoading] = useState(!colorsAndMaterials);
    const [error, setError] = useState(null);
    const [selectedColors, setSelectedColors] = useState({});

    useEffect(() => {
        if (!colorsAndMaterials) {
            setError(
                "Hiányzik a modellhez tartozó színinformáció (colors_and_materials_for_model)."
            );
            setLoading(false);
        } else {
            setError(null);
            setLoading(false);
        }
    }, [colorsAndMaterials]);

    const { categories, categoryParts } = useMemo(() => {
        if (!colorsAndMaterials) {
            return { categories: [], categoryParts: {} };
        }

        const groupKeys = Object.keys(colorsAndMaterials)
            .filter((k) => k.startsWith("color_group_"))
            .sort((a, b) => {
                const na = parseInt(a.replace("color_group_", ""), 10) || 0;
                const nb = parseInt(b.replace("color_group_", ""), 10) || 0;
                return na - nb;
            });

        const categoriesTmp = groupKeys.map((gk, idx) => ({
            key: gk,
            title: `Szín ${idx + 1}`,
            index: idx + 1,
        }));

        const categoryPartsTmp = groupKeys.reduce((acc, gk) => {
            const parts = Array.isArray(colorsAndMaterials[gk])
                ? colorsAndMaterials[gk]
                : [];
            acc[gk] = parts.map((p) => ({ key: slugify(p), label: p }));
            return acc;
        }, {});

        return {
            categories: categoriesTmp,
            categoryParts: categoryPartsTmp,
        };
    }, [colorsAndMaterials]);

    const getColorsForCategory = (categoryIndex) => {
        if (categoryIndex === 1) {
            return shoe_colors.filter((color) => color.color_1);
        }
        if (categoryIndex === 2) {
            return shoe_colors.filter((color) => color.color_2);
        }
        if (categoryIndex === 3) {
            return shoe_colors.filter((color) => color.color_3);
        }
        return [];
    };

    const visibleCategories = useMemo(() => {
        return categories.filter((cat) => {
            const hasParts = (categoryParts[cat.key] || []).length > 0;
            const hasColors = getColorsForCategory(cat.index).length > 0;
            return hasParts && hasColors;
        });
    }, [categories, categoryParts, shoe_colors]);

    useEffect(() => {
        if (!visibleCategories.length) return;

        const stored = order.hmeliorder?.hmeliorder_leather_color || "";
        if (!stored.trim()) {
            setSelectedColors({});
            return;
        }

        const storedParts = stored.split(",").map((part) => part.trim());
        const mapped = {};

        visibleCategories.forEach((cat, idx) => {
            const storedLabel = storedParts[idx];
            if (!storedLabel) return;

            const categoryColors = getColorsForCategory(cat.index);
            const match = categoryColors.find((color) => color.color_text === storedLabel);

            if (match) {
                mapped[cat.key] = match.id;
            }
        });

        setSelectedColors((prev) => {
            const same =
                Object.keys(prev).length === Object.keys(mapped).length &&
                Object.keys(prev).every((key) => prev[key] === mapped[key]);

            return same ? prev : mapped;
        });
    }, [order.hmeliorder?.hmeliorder_leather_color, visibleCategories, shoe_colors]);

    const buildLeatherColorValue = (selectionMap) =>
        visibleCategories
            .map((cat) => {
                const selectedId = selectionMap[cat.key];
                const categoryColors = getColorsForCategory(cat.index);
                return categoryColors.find((color) => color.id === selectedId)?.color_text || "";
            })
            .join(", ");

    const applySelection = (selectionMap) => {
        const nextValue = buildLeatherColorValue(selectionMap);

        if ((order.hmeliorder?.hmeliorder_leather_color || "") === nextValue) {
            return;
        }

        const colorIdsByCategory = visibleCategories.map((cat) => {
            const selectedId = selectionMap[cat.key];
            const categoryColors = getColorsForCategory(cat.index);
            return categoryColors.find((color) => color.id === selectedId)?.baseId ?? null;
        });

        updateOrder({
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_leather_color: nextValue,
                hmeliorder_color_1: colorIdsByCategory[0] ?? null,
                hmeliorder_color_2: colorIdsByCategory[1] ?? null,
                hmeliorder_color_3: colorIdsByCategory[2] ?? null
            },
        });
    };

    const handleColorSelect = (catKey, colorId) => {
        setSelectedColors((prev) => {
            const updated = { ...prev, [catKey]: colorId };
            applySelection(updated);
            return updated;
        });
    };

    const handleBackClick = () => {
        navigate("/EgySzinu");
    };

    const canProceed =
        visibleCategories.length > 0 &&
        visibleCategories.every((cat) => {
            const selected = selectedColors[cat.key];
            return selected !== null && selected !== undefined && selected !== "";
        });

    const handleSubmit = async () => {
        if (!canProceed) {
            showErrorToast("Minden színcsoporthoz válassz színt!");
            return;
        }

        const payload = {
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_leather_color: buildLeatherColorValue(selectedColors),
            },
        };

        updateOrder(payload);

        const result = await saveOrder(payload);
        if (result.success) {
            showSuccessToast("A rendelés sikeresen mentve!");
            navigate("/TuzesSzine");
        } else {
            showErrorToast("Hiba történt a rendelés mentésekor!");
        }
    };

    if (loading) {
        return (
            <TablerLayout title="Többszínű cipő – színek kiválasztása">
                <div className="text-center">Betöltés...</div>
            </TablerLayout>
        );
    }

    if (error) {
        return (
            <TablerLayout title="Többszínű cipő – színek kiválasztása">
                <div className="text-danger text-center">
                    Hiba történt a színek betöltésekor: {error}
                </div>
            </TablerLayout>
        );
    }

    return (
        <TablerLayout title="Többszínű cipő – színek kiválasztása">
            <div className="row justify-content-center">
                <div className="col-md-10 mb-4">
                    <div className="card-body">
                        <div className="row g-4 mb-4">
                            {visibleCategories.map((cat) => {
                                const categoryColors = getColorsForCategory(cat.index);

                                return (
                                    <div key={cat.key} className="col-12 col-md-4">
                                        <div className="p-3 h-100 d-flex flex-column">
                                            <div className="mb-2">
                                                <h6 className="mb-1 text-center">{cat.title}</h6>
                                            </div>

                                            <div className="mb-3">
                                                {(categoryParts[cat.key] || []).map((part) => (
                                                    <div
                                                        key={part.key}
                                                        style={{
                                                            padding: "10px",
                                                            marginBottom: "6px",
                                                            backgroundColor: "white",
                                                            border: "1px solid #e2e8f0",
                                                            fontSize: "15px",
                                                            fontWeight: "500",
                                                            color: "black",
                                                            justifyContent: "center",
                                                            textAlign: "center",
                                                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                                        }}
                                                    >
                                                        {part.label}
                                                    </div>
                                                ))}
                                            </div>

                                            <div
                                                style={{
                                                    marginTop: "auto",
                                                    padding: "10px",
                                                    border: "1px solid #e2e8f0",
                                                    backgroundColor: "#f9fafb",
                                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        marginBottom: "10px",
                                                        padding: "10px",
                                                        backgroundColor: "#ffffff",
                                                        border: "1px solid #e5e7eb",
                                                        fontSize: "14px",
                                                        fontWeight: "500",
                                                        color: "black",
                                                        textAlign: "center",
                                                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                                    }}
                                                >
                                                    {selectedColors[cat.key]
                                                        ? `Kiválasztott szín: ${
                                                              categoryColors.find(
                                                                  (c) => c.id === selectedColors[cat.key]
                                                              )?.color_text
                                                          }`
                                                        : "Nincs kiválasztva szín."}
                                                </div>

                                                <div
                                                    className="scrollable-div"
                                                    style={{
                                                        maxHeight: "300px",
                                                        overflowY: "auto",
                                                        border: "1px solid #e2e8f0",
                                                        padding: "10px",
                                                        backgroundColor: "#ffffff",
                                                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                                    }}
                                                >
                                                    {categoryColors.map((color) => (
                                                        <div
                                                            key={color.id}
                                                            className={`color-option ${
                                                                selectedColors[cat.key] === color.id
                                                                    ? "selected"
                                                                    : ""
                                                            }`}
                                                            style={{
                                                                padding: "10px",
                                                                marginBottom: "6px",
                                                                cursor: "pointer",
                                                                border: "1px solid #e2e8f0",
                                                                backgroundColor:
                                                                    selectedColors[cat.key] === color.id
                                                                        ? "#81c94a"
                                                                        : "#f8fafc",
                                                                color: "black",
                                                                fontWeight:
                                                                    selectedColors[cat.key] === color.id
                                                                        ? "600"
                                                                        : "500",
                                                                boxShadow:
                                                                    selectedColors[cat.key] === color.id
                                                                        ? "0 2px 4px rgba(0, 0, 0, 0.2)"
                                                                        : "0 1px 2px rgba(0, 0, 0, 0.1)",
                                                            }}
                                                            onClick={() =>
                                                                handleColorSelect(cat.key, color.id)
                                                            }
                                                        >
                                                            {color.color_text}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="card-footer d-flex justify-content-between mt-4">
                        <StepperNavButton
                            side="left"
                            label="Vissza"
                            onClick={handleBackClick}
                        />
                        <StepperNavButton
                            side="right"
                            label="Tovább"
                            onClick={handleSubmit}
                            disabled={!canProceed || loading}
                        />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default TobbSzinu;